import SharedDialog from '../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, updateTrashedDocsStatus } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { Doc } from '../../../../types';
import { Dispatch, SetStateAction, useState } from 'react';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DriveSearchRestoreDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDoc: Doc;
  searchedDocs: Doc[];
  setSearchedDocs: Dispatch<SetStateAction<Doc[]>>;
  changeLogRoles: string[];
}

export default function DriveSearchRestoreDialog({
  open,
  handleClose,
  selectedDoc,
  searchedDocs,
  setSearchedDocs,
  changeLogRoles,
}: DriveSearchRestoreDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      updateTrashedDocsStatus(
        selectedDoc.ownerEmail,
        [selectedDoc],
        false,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'The document or folder was restored from trash successfully!',
      });

      const newList = searchedDocs.filter((doc) => doc.id !== selectedDoc.id);
      const updatedDoc: Doc = {
        ...selectedDoc,
        trashed: false,
      };
      newList.push(updatedDoc);
      setSearchedDocs(newList);

      // update all folders
      queryClient.invalidateQueries({ queryKey: [UserKeys.DriveFolders] });

      // update docs
      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments, selectedDoc.ownerEmail],
      });
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue restoring the selected document or folder from trash. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.drive.drive_search.restore_drive_content, {
        module: ModuleList.modules.drive.name,
        section: ModuleList.modules.drive.sections.driveSearch,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanRestoreDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Restore Document"
      dialogContent={
        <Box>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            Restore the document: <strong>{selectedDoc.title}</strong> from
            trash?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Restore Document',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
